import type { CostingBasis } from '@servwell/shared';

/**
 * What the figures beside a margin are measured over, in words, for the two panels that show
 * one: the job's money panel and the project's (ops insights ticket 10 review). One module, so
 * the panels cannot drift apart.
 *
 * Revenue is the server's (`reports/revenue.ts`): invoice subtotals, before tax, with late fees
 * out, like every report's. Until something is invoiced the margin is measured against the
 * approved quotes instead, and a quote is not revenue: the figure is named for the quote it is,
 * and a quote has no late fee to leave out. Collected is what was paid on the invoices, which
 * takes in the tax and any late fee paid; unlabelled, a paid-up project reads
 * Collected > Revenue.
 */
export const REVENUE_BASIS = 'Before tax · late fees out';
export const QUOTE_BASIS = 'Before tax';
export const COLLECTED_BASIS = 'Tax and late fees included';

/** Each panel's approved quotes, in the words of its "vs. approved quote(s)" badge. */
const QUOTED_LABEL = { job: 'Approved quote', project: 'Approved quotes' } as const;

/**
 * The name of the figure the margin is measured against, and the line under it. With nothing
 * billed yet the figure is Revenue's dash, and Revenue is what will fill it.
 */
export function revenueFigure(
  panel: keyof typeof QUOTED_LABEL,
  basis: CostingBasis,
): { label: string; line: string } {
  return basis === 'quoted'
    ? { label: QUOTED_LABEL[panel], line: QUOTE_BASIS }
    : { label: 'Revenue', line: REVENUE_BASIS };
}
